import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, User, Check, ArrowLeft, CalendarIcon, ClockIcon, ChevronLeft, ChevronRight, Globe, Zap, Sun, Mountain, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';
import { getDateKeyInTimezone } from '@/lib/timezone/date-utils';
import { cn } from '@/lib/utils';

interface BookingStep {
  id: string;
  title: string;
  subtitle: string;
  content: React.ReactNode;
  duration: number;
}

const BookingFlowDemo: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false); // Start paused
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedTimezone, setSelectedTimezone] = useState<string>('America/Chicago');
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });
  const [guests, setGuests] = useState<string[]>(['']);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [typingText, setTypingText] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });
  const [isTyping, setIsTyping] = useState(false);
  const [buttonPressed, setButtonPressed] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // Intersection Observer to detect when component is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          setIsPlaying(true);
        } else {
          setIsInView(false);
          setIsPlaying(false);
          // Reset animation state when out of view
          setCurrentStep(0);
          setSelectedDate(null);
          setSelectedTimeSlot('');
          setTypingText({ name: '', company: '', email: '', message: '' });
          setButtonPressed(false);
          setShowConfetti(false);
        }
      },
      {
        threshold: 0.3, // Start animation when 30% of component is visible
        rootMargin: '0px 0px -100px 0px' // Start slightly before fully in view
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const steps = [
    { key: 'time', label: 'Select Time', number: 1 },
    { key: 'form', label: 'Your Details', number: 2 },
    { key: 'confirmation', label: 'Confirmation', number: 3 },
  ];

  const demoSteps: BookingStep[] = [
    {
      id: 'time',
      title: 'Select Time',
      subtitle: 'Choose a time that works for you',
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 h-[800px]">
          {/* Left Panel - Meeting Information with Video Background */}
          <div className="relative rounded-l-lg overflow-hidden">
            {/* Video Background */}
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src="https://videos.pexels.com/video-files/29700435/12771815_1440_2560_30fps.mp4" type="video/mp4" />
            </video>
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-black/40 to-black/60"></div>
            
            <div className="relative z-10 p-8 h-full flex flex-col justify-between">
              <div className="text-white">
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mb-4"
                    >
                    <img
                        src="/assets/images/avatars/avatar-demo.jpeg"
                        alt="Isaac"
                        className="w-16 h-16 rounded-full object-cover shadow-lg border-white/20"
                    />
                    </motion.div>                
                <h2 className="text-3xl font-bold mb-2">Meet with Isaac</h2>
                <p className="text-lg opacity-90">Book a 1:1 meeting with me.</p>
              </div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="bg-black/30 backdrop-blur-sm rounded-lg p-4 text-white"
              >
                <div className="flex items-center space-x-3">
                  <div className="bg-white rounded-lg p-2 text-black text-center min-w-[50px]">
                    <div className="text-xs font-medium">AUG</div>
                    <div className="text-xl font-bold">13</div>
                  </div>
                  <div>
                    <div className="flex items-center space-x-1">
                      <span className="font-medium">Wed, August 13</span>
                      <Globe className="w-4 h-4" />
                      <span className="text-sm">CDT</span>
                    </div>
                    <div className="text-sm opacity-80">Select a timeslot</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Middle Panel - Calendar */}
          <div className="bg-white p-6 flex flex-col">
            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-4">Select date and time</h3>
            </div>

            <div className="flex-1">
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day, i) => (
                  <div key={i} className="text-center text-sm font-medium text-muted-foreground p-2">
                    {day}
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: 42 }, (_, i) => {
                  const day = i - 4;
                  const isCurrentMonth = day > 0 && day <= 31;
                  const isSelected = selectedDate && selectedDate.getDate() === day;
                  const isAvailable = [6, 7, 8, 10, 11, 12, 13, 14, 15, 17, 18, 19, 20, 21, 22, 24, 25, 26, 27, 28, 29].includes(day);
                  
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.01 }}
                      className={`aspect-square p-1`}
                    >
                      {isCurrentMonth && (
                        <motion.div
                          className={`w-full h-full rounded-full flex items-center justify-center text-sm font-medium ${
                            isSelected
                              ? 'bg-blue-600 text-white'
                              : isAvailable
                              ? 'bg-blue-100 text-black'
                              : 'text-muted-foreground/30'
                          }`}
                          animate={isSelected ? { scale: [1, 1.5, 1] } : {}}
                          transition={{ delay: 3, duration: 0.7 }}
                        >
                          {day}
                        </motion.div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Panel - Time Slots */}
          <div className="bg-white p-6 rounded-r-lg">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Wed, August 13</h3>
              <p className="text-gray-600">2025</p>
            </div>

            {/* Optimal Times Section */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 mb-6 border border-purple-100 shadow-sm"
            >
              <div className="flex items-center space-x-2 mb-4">
                <Zap className="w-5 h-5 text-purple-600" />
                <span className="text-lg font-semibold text-purple-800">Optimal Times</span>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Mountain className="w-4 h-4 text-orange-500" />
                    <span className="text-sm font-medium text-gray-800">Morning 3 slots</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {['9:00 AM', '9:30 AM', '10:00 AM'].map((time, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + index * 0.1 }}
                      >
                        <motion.div
                          animate={selectedTimeSlot === time ? { 
                            scale: [1, 0.9, 1.2, 1],
                            //backgroundColor: ['#ffffff', '#3b82f6'],
                            //color: ['#000000', '#ffffff', '#ffffff', '#000000']
                          } : {}}
                          transition={{ delay: 3, duration: 0.7 }}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            className={
                                cn(
                                    "w-full text-sm border-gray-300 bg-white text-gray-900 hover:border-blue-500 hover:bg-blue-50 font-medium",
                                    selectedTimeSlot === time ? "bg-blue-600 text-white" : ""
                                )
                            }
                          >
                            {time}
                          </Button>
                        </motion.div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="text-sm text-purple-600 mt-3"
              >
                Suggested based on the host's circadian rhythm for this meeting type.{' '}
                <span className="underline cursor-pointer font-medium">Learn more</span>
              </motion.p>
            </motion.div>

            {/* Additional Afternoon Slots */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <div className="flex items-center space-x-2 mb-3">
                <Sun className="w-4 h-4 text-orange-500" />
                <span className="font-medium text-gray-800">Afternoon 4 slots</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {['12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM'].map((time, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1 + index * 0.05 }}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-sm border-gray-300 bg-white text-gray-900 hover:border-blue-500 hover:bg-blue-50 font-medium"
                    >
                      {time}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      ),
      duration: 4000
    },
    {
      id: 'form',
      title: 'Your Details',
      subtitle: 'Tell us about yourself',
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 h-[800px]">
          {/* Left Panel - Meeting Information with Video Background */}
          <div className="relative rounded-l-lg overflow-hidden">
            {/* Video Background */}
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src="https://videos.pexels.com/video-files/29700435/12771815_1440_2560_30fps.mp4" type="video/mp4" />
            </video>
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-black/40 to-black/60"></div>
            
            <div className="relative z-10 p-8 h-full flex flex-col justify-between">
              <div className="text-white">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mb-4"
                >
                  <img
                    src="/assets/images/avatars/avatar-demo.jpeg"
                    alt="Isaac"
                    className="w-16 h-16 rounded-full object-cover shadow-lg border-white/20"
                  />
                </motion.div>
                <h2 className="text-3xl font-bold mb-2">Meet with Isaac</h2>
                <p className="text-lg opacity-90">Book a 1:1 meeting with me.</p>
              </div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-gradient-to-r from-purple-600 to-orange-500 rounded-lg p-4 text-white"
              >
                <div className="flex items-center space-x-3">
                  <div className="bg-white rounded-lg p-2 text-black text-center min-w-[50px]">
                    <div className="text-xs font-medium">AUG</div>
                    <div className="text-xl font-bold">13</div>
                  </div>
                  <div>
                    <div className="flex items-center space-x-1">
                      <span className="font-medium">Wed, August 13</span>
                      <Globe className="w-4 h-4" />
                      <span className="text-sm">CDT</span>
                    </div>
                    <div className="text-sm opacity-90">Optimal 9:00 AM - 9:30 AM</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Right Panel - Contact Form */}
          <div className="bg-white p-6 flex flex-col">
            {/* Back Button */}
            <div className="mb-6">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" disabled>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>

            {/* Time Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-r from-purple-600 to-orange-500 rounded-lg p-4 text-white mb-6"
            >
              <div className="flex items-center space-x-3">
                <div className="bg-white rounded-lg p-2 text-black text-center min-w-[50px]">
                  <div className="text-xs font-medium">AUG</div>
                  <div className="text-xl font-bold">13</div>
                </div>
                <div>
                  <div className="flex items-center space-x-1">
                    <span className="font-medium">Wed, August 13</span>
                    <Globe className="w-4 h-4" />
                    <span className="text-sm">CDT</span>
                  </div>
                  <div className="text-sm opacity-90">Optimal 9:00 AM - 9:30 AM</div>
                </div>
              </div>
            </motion.div>

            {/* Form Section */}
            <div className="flex-1">
              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-2">Enter contact info</h3>
                <p className="text-muted-foreground">Please provide your information to confirm the booking.</p>
              </div>

                             {/* Form Fields */}
               <div className="space-y-4">
                 <div>
                   <Label htmlFor="name" className="text-sm font-medium text-gray-800">
                     Full Name *
                   </Label>
                   <Input
                     id="name"
                     placeholder="Enter your full name"
                     className="mt-1 text-black placeholder:text-black"
                     value={typingText.name}
                     onChange={(e) => setTypingText({ ...typingText, name: e.target.value })}
                   />
                 </div>
                 
                 <div>
                   <Label htmlFor="company" className="text-sm font-medium text-gray-800">
                     Company (Optional)
                   </Label>
                   <Input
                     id="company"
                     placeholder="Enter your company name"
                     className="mt-1 text-black placeholder:text-gray-500"
                     value={typingText.company}
                     onChange={(e) => setTypingText({ ...typingText, company: e.target.value })}
                   />
                 </div>
                 
                 <div>
                   <Label htmlFor="email" className="text-sm font-medium text-gray-800">
                     Email Address *
                   </Label>
                   <Input
                     id="email"
                     type="email"
                     placeholder="Enter your email address"
                     className="mt-1 text-black placeholder:text-gray-500"
                     value={typingText.email}
                     onChange={(e) => setTypingText({ ...typingText, email: e.target.value })}
                   />
                 </div>
                 
                 <div>
                   <Label htmlFor="message" className="text-sm font-medium text-gray-800">
                     Message (Optional)
                   </Label>
                   <Textarea
                     id="message"
                     placeholder="Add any additional details or questions..."
                     className="mt-1 min-h-[50px] resize-none text-black placeholder:text-gray-500"
                     value={typingText.message}
                     onChange={(e) => setTypingText({ ...typingText, message: e.target.value })}
                   />
                 </div>
               </div>

              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2 }}
                className="mt-8"
              >
            <motion.div
                    animate={buttonPressed ? { 
                      scale: [1, 0.9, 1.2, 1],
                      opacity: 0.9
                    } : { 
                      scale: 1,
                      opacity: 1
                    }}
                    transition={{ duration: 0.2 }}
                  >
                  <Button className="w-full bg-black py-3 text-lg">
                    Confirm Booking
                  </Button>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      ),
      duration: 4000
    },
    {
      id: 'confirmation',
      title: 'Confirmation',
              subtitle: 'Your meeting has been scheduled',
        content: (
          <div className="max-w-2xl mx-auto space-y-6 h-[800px] relative">
            {/* Confetti Animation */}
            {showConfetti && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 pointer-events-none"
              >
                {[...Array(50)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ 
                      x: Math.random() * window.innerWidth, 
                      y: -20,
                      rotate: 0,
                      scale: 0
                    }}
                    animate={{ 
                      y: window.innerHeight + 100,
                      rotate: 360,
                      scale: 1
                    }}
                    transition={{ 
                      duration: 3 + Math.random() * 2,
                      delay: Math.random() * 2,
                      ease: "easeOut"
                    }}
                    className="absolute w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full"
                    style={{
                      left: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 2}s`
                    }}
                  />
                ))}
              </motion.div>
            )}
            
            <div className="bg-green-50 border border-green-200 mt-20 rounded-lg p-4 flex flex-col items-center">
            <div className="flex flex-col items-center mb-2">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
                className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mb-2"
              >
                <Check className="h-4 w-4 text-green-600" />
              </motion.div>
              <h3 className="font-medium text-green-900">Strategy Session</h3>
              <p className="text-sm text-green-700">Friday, January 15, 2024</p>
            </div>
            
            <div className="flex flex-col items-center space-y-2 text-sm text-green-700">
              <div className="flex items-center space-x-1">
                <Clock className="h-4 w-4" />
                <span>9:00 AM</span>
              </div>
              <div className="flex items-center space-x-1">
                <User className="h-4 w-4" />
                <span>30 minutes</span>
              </div>
            </div>
          </div>

          <div className="text-center space-y-2 flex flex-col items-center">
            <p className="text-sm text-muted-foreground">
              A calendar invitation has been sent to your email.
            </p>
            <div className="flex space-x-2 justify-center">
              <Button variant="outline" size="sm" disabled>
                Book Another Time
              </Button>
            </div>
          </div>
        </div>
      ),
      duration: 4000
    }
  ];

  // Simulate typing effect and time selection
  useEffect(() => {
    if (currentStep === 0 && isPlaying) {
      // Reset selections when step starts
      setSelectedTimeSlot('');
      setSelectedDate(null);
      
      // Wait 2 seconds before starting animations
      setTimeout(() => {
        // Step 1: Select date 13 after 2 second delay
        setTimeout(() => {
          setSelectedDate(new Date(2025, 7, 13)); // August 13, 2025
        }, 1000);
        
        // Step 2: Select 9:00 AM optimal time after date selection
        setTimeout(() => {
          setSelectedTimeSlot('9:00 AM');
        }, 2500);
        
        // Step 3: Go to form step after time selection
        setTimeout(() => {
          setCurrentStep(1);
        }, 4000);
      }, 2000); // 2 second initial delay
    }
  }, [currentStep, isPlaying]);

  useEffect(() => {
    if (currentStep === 1 && isPlaying) {
      // Reset typing text when step starts
      setTypingText({ name: '', company: '', email: '', message: '' });
      
      // Simulate typing effect for form fields
      const typeText = (text: string, field: keyof typeof typingText, delay: number) => {
        setTimeout(() => {
          let currentIndex = 0;
          const interval = setInterval(() => {
            if (currentIndex <= text.length) {
              setTypingText(prev => ({
                ...prev,
                [field]: text.slice(0, currentIndex)
              }));
              currentIndex++;
            } else {
              clearInterval(interval);
            }
          }, 80); // Typing speed
        }, delay);
      };

      // Start typing each field sequentially (one at a time)
      typeText('John Smith', 'name', 500);
      typeText('TechCorp Inc.', 'company', 2000); // Wait for name to complete
      typeText('john.smith@techcorp.com', 'email', 3500); // Wait for company to complete
      typeText('Looking forward to discussing our strategy and goals for the upcoming quarter.', 'message', 5000); // Wait for email to complete
      
      // Step 4: Simulate button press immediately after message field completes
      setTimeout(() => {
        setButtonPressed(true);
        // Reset button after a short delay and go to confirmation
        setTimeout(() => {
          setButtonPressed(false);
          // Immediately go to confirmation step
          setCurrentStep(2);
        }, 200);
      }, 11400); // Message completes at ~11.4 seconds, button presses immediately after
    }
  }, [currentStep, isPlaying]);

  // Handle confirmation step
  useEffect(() => {
    if (currentStep === 2 && isPlaying) {
      // Step 5: Show confetti immediately when confirmation step starts
      setShowConfetti(true);
      
      // Stay on confirmation for 4 seconds, then loop back
      setTimeout(() => {
        setShowConfetti(false);
        setCurrentStep(0);
      }, 4000);
    }
  }, [currentStep, isPlaying]);

  useEffect(() => {
    if (!isPlaying) return;

    // Only auto-advance if we're not manually controlling the flow
    if (currentStep === 0 || currentStep === 1) {
      return; // Manual control for these steps
    }

    const timer = setTimeout(() => {
      setCurrentStep((prev) => (prev + 1) % demoSteps.length);
    }, demoSteps[currentStep].duration);

    return () => clearTimeout(timer);
  }, [currentStep, isPlaying, demoSteps]);

  const currentStepData = demoSteps[currentStep];

  return (
    <div ref={containerRef} className="w-full max-w-6xl mx-auto relative">
      <Card className="shadow-xl border-0 bg-white overflow-hidden">
        <CardContent className="p-0">
          {/* Content */}
          <div className="p-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {currentStepData.content}
              </motion.div>
            </AnimatePresence>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingFlowDemo; 